import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ReplaySubject, takeUntil, firstValueFrom } from 'rxjs';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-input';
import { ToastrService } from '../../services/toastr-service';
import { RepairLocationService } from '../../services/repair-location.service';
import { RepairLocationListItem } from '../../models/repair-location-list-item.model';
import { ErrorComponent } from '../../shared-components/error/error.component';
import { MasterDataAuditLogComponent } from '../../shared-components/master-data-audit-log/master-data-audit-log.component';

@Component({
  selector: 'app-create-repair-location',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ErrorComponent,
    MasterDataAuditLogComponent,
  ],
  templateUrl: './create-repair-location.component.html',
  styleUrls: ['./create-repair-location.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CreateRepairLocationComponent implements OnInit, OnDestroy {
  formSubmitErrors = signal<string[]>([]);
  submitLoader = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  showCancelDialog = signal<boolean>(false);
  openAuditLog = false;
  repairLocationCode: string | null = null;
  repairLocationData = signal<RepairLocationListItem | null>(null);

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  createRepairLocationForm = this.formBuilder.group({});

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastrService: ToastrService,
    private readonly repairLocationService: RepairLocationService,
  ) {}

  ngOnInit(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    this.repairLocationCode = this.route.snapshot.paramMap.get('code');
    this.addControlsToTheFormGroup();
    if (this.repairLocationCode) {
      this.isEditMode.set(true);
      await this.loadRepairLocationData(this.repairLocationCode);
    } else {
      this.isEditMode.set(false);
      this.createRepairLocationForm.get('code')?.enable({ emitEvent: false });
    }
  }

  private async loadRepairLocationData(code: string): Promise<void> {
    try {
      const data = await firstValueFrom(this.repairLocationService.getRepairLocationByCode(code));
      if (!data) {
        throw new Error('No data returned');
      }
      this.repairLocationData.set(data);
      this.populateFormWithData(data);
      this.createRepairLocationForm.get('code')?.disable({ emitEvent: false });
    } catch {
      this.toastrService.showToastr('Failed to load repair location data', 'error');
      this.router.navigate(['/master-data/repair-location']);
    }
  }

  private populateFormWithData(data: RepairLocationListItem): void {
    this.createRepairLocationForm.patchValue({
      code: data.code,
      name: data.name,
    });
    this.createRepairLocationForm.markAsPristine();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private addControlsToTheFormGroup(): void {
    this.createRepairLocationForm.addControl(
      'code',
      this.formBuilder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(50)]),
    );
    this.createRepairLocationForm.addControl(
      'name',
      this.formBuilder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(200)]),
    );
  }

  isControlInErrorState(control: FormControl | AbstractControl | null | undefined): boolean | null {
    return control?.invalid && control?.touched ? true : null;
  }

  onCancel(): void {
    if (this.createRepairLocationForm.dirty) {
      this.showCancelDialog.set(true);
    } else {
      this.navigateToRepairLocation();
    }
  }

  confirmCancel(): void {
    this.showCancelDialog.set(false);
    this.navigateToRepairLocation();
  }

  keepEditing(): void {
    this.showCancelDialog.set(false);
  }

  private navigateToRepairLocation(): void {
    this.router.navigate(['/master-data/repair-location']);
  }

  onSubmit(): void {
    if (this.createRepairLocationForm.invalid) {
      for (const key of Object.keys(this.createRepairLocationForm.controls)) {
        this.createRepairLocationForm.get(key)?.markAsTouched();
      }
      this.toastrService.showToastr('Please fill in all required fields correctly', 'warning');
      return;
    }
    if (this.isEditMode()) {
      this.updateRepairLocation();
    } else {
      this.createRepairLocation();
    }
  }

  private createRepairLocation(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);
    const formValue = this.createRepairLocationForm.getRawValue() as { code: string; name: string };

    this.repairLocationService
      .createRepairLocation({ code: formValue.code, name: formValue.name })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: RepairLocationListItem) => {
          this.submitLoader.set(false);
          this.createRepairLocationForm.reset();
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr(`Repair Location '${response.code}' created successfully`, 'success');
          this.router.navigate(['/master-data/repair-location']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleRepairLocationError(error, 'creating');
        },
      });
  }

  private updateRepairLocation(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);
    const formValue = this.createRepairLocationForm.getRawValue() as { code: string; name: string };

    this.repairLocationService
      .updateRepairLocation(this.repairLocationCode!, { name: formValue.name })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: RepairLocationListItem) => {
          this.submitLoader.set(false);
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr(`Repair Location '${response.code}' updated successfully`, 'success');
          this.router.navigate(['/master-data/repair-location']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleRepairLocationError(error, 'updating');
        },
      });
  }

  private handleRepairLocationError(error: HttpErrorResponse, operation: string): void {
    this.submitLoader.set(false);
    if (error.status === 400) {
      this.formSubmitErrors.set(['Invalid data. Please check your inputs.']);
      this.toastrService.showToastr('Invalid data. Please check your inputs.', 'error');
    } else if (error.status === 409) {
      this.formSubmitErrors.set(['This repair location already exists.']);
      this.toastrService.showToastr('This repair location already exists.', 'error');
    } else if (error.status === 422 && error.error?.violations) {
      const errors = (error.error.violations as { message: string }[]).map((v) => v.message);
      this.formSubmitErrors.set(errors);
      this.toastrService.showToastr('Validation errors occurred. Please check the form.', 'error');
    } else {
      this.formSubmitErrors.set([`Failed to ${operation} repair location. Please try again.`]);
      this.toastrService.showToastr(`Failed to ${operation} repair location. Please try again.`, 'error');
    }
  }

  onAuditLogClick(): void {
    if (!this.repairLocationCode) {
      this.toastrService.showToastr('Repair location code not available for audit log', 'error');
      return;
    }
    this.openAuditLog = true;
  }

  readonly auditLogFetcher = (_transactionCode: string, isSystemLog: boolean, page: number, limit: number) =>
    this.repairLocationService.getAuditLogs(this.repairLocationCode!, isSystemLog, page, limit);
}
